import { useState, useEffect } from 'react';
import type { Badge, Passport, AchievementsData } from '../types/achievement';
import { achievementsApi } from '../services/achievementsApi';

export const useAchievements = () => {
  const [achievements, setAchievements] = useState<AchievementsData>({
    coreCategories: [],
    skillstree: [],
    badges: [],
    badgesCount: null,
    badgesTotal: null,
    passports: [],
    passportsCount: null,
    loading: true,
    error: null
  });

  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Load all achievements data
  const loadAchievements = async () => {
    setAchievements(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const [badges, passports, coreCategories, skillstree] = await Promise.all([
        achievementsApi.getBadges(),
        achievementsApi.getPassports(),
        achievementsApi.getCoreCategories(),
        achievementsApi.getSkillstrees(),
      ]);
      
      let badgesCount = badges?.length ? badges.filter(p => p.earned_at !== null).length : 0;
      let passportsCount = passports?.length ? passports.filter(p => p.completed_at !== null).length : 0;
      
      let badgesTotal = countAllMicroSkills(skillstree);

      setAchievements({
        badges,
        badgesCount,
        badgesTotal,
        passports,
        passportsCount,
        coreCategories, 
        skillstree,
        loading: false,
        error: null
      });
      setLastUpdated(new Date());
    } catch (error) {
      setAchievements(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to load achievements'
      }));
    }
  };

  // Refresh achievements data
  const refreshAchievements = async () => {
    await loadAchievements();
  };

  // Verify a badge
  const verifyBadge = async (uuid: string) => {
    try {
      const result = await achievementsApi.verifyBadge(uuid);
      return result;
    } catch (error) {
      console.error('Verification failed:', error);
      throw error;
    }
  };

  // Verify a passport
  const verifyPassport = async (uniqueCode: string) => {
    try {
      const result = await achievementsApi.verifyPassport(uniqueCode);
      return result;
    } catch (error) {
      console.error('Verification failed:', error);
      throw error;
    }
  };

  // Get specific badge by UUID
  const getBadgeByUuid = (uuid: string): Badge | undefined => {
    return achievements.badges.find(badge => badge.uuid === uuid);
  };

  // Get specific passport by unique code
  const getPassportByCode = (uniqueCode: string): Passport | undefined => {
    return achievements.passports.find(passport => passport.unique_code === uniqueCode);
  };

  const countAllMicroSkills = (skillstrees: any[]): number => {
    return skillstrees.reduce((total, tree) => {
      return total + tree.versions.reduce((count: number, version: any) => {
        return count + version.micro_skills.length;
      }, 0);
    }, 0);
  };

  // Load data on component mount
  useEffect(() => {
    loadAchievements();
  }, []);

  return {
    ...achievements,
    lastUpdated,
    refreshAchievements,
    verifyBadge,
    verifyPassport,
    getBadgeByUuid,
    getPassportByCode
  };
};